// src/components/profile/ResetPasswordForm.js - Set a new password from an emailed reset link
// The link (sent by the API through SendGrid) points at our own domain:
// /?mode=resetPassword&oobCode=... The one-time code is consumed only when the
// new password is submitted, never on page load (link scanners open links first).
import { useState } from 'react';
import PropTypes from 'prop-types';
import { confirmPasswordReset } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { validatePassword } from '../../utils/passwordValidation';

const ResetPasswordForm = ({ oobCode, onDone, onRequestNewLink }) => {
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setPasswordError(e.target.value ? validatePassword(e.target.value) : '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validation = validatePassword(password);
    if (validation) {
      setPasswordError(validation);
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess(true);
    } catch (err) {
      console.error('Error resetting password:', err.code);
      switch (err.code) {
        case 'auth/expired-action-code':
        case 'auth/invalid-action-code':
          setError('This reset link is invalid, has expired, or has already been used. Request a new one.');
          break;
        case 'auth/user-disabled':
        case 'auth/user-not-found':
          setError('This account cannot be reset. Contact your administrator.');
          break;
        case 'auth/weak-password':
          setError('That password is too weak. Choose a stronger one.');
          break;
        case 'auth/network-request-failed':
          setError('Network error. Please check your connection and try again.');
          break;
        default:
          setError('Failed to reset your password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-form">
        <h3 className="auth-form-title">Password Updated</h3>
        <p className="text-sm text-gray-700 mb-4">Your password has been changed. Sign in with your new password.</p>
        <button type="button" className="auth-button" onClick={onDone}>
          Sign In
        </button>
      </div>
    );
  }

  return (
    <div className="auth-form">
      <h3 className="auth-form-title">Choose a New Password</h3>

      {error && (
        <div className="alert alert-error mb-4">
          {error}
          <button onClick={() => setError(null)} className="alert-close">×</button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="new-password" className="form-label">
            New Password <span className="text-error">*</span>
          </label>
          <input
            id="new-password"
            type="password"
            value={password}
            onChange={handlePasswordChange}
            required
            disabled={loading}
            minLength="8"
            className="form-input"
            placeholder="Create a secure password (8+ characters)"
            autoComplete="new-password"
          />
          <div className="password-requirements">
            <p>Requirements:</p>
            <ul>
              <li className={password.length >= 8 ? 'valid' : ''}>8+ characters</li>
              <li className={/[A-Z]/.test(password) ? 'valid' : ''}>Uppercase letter (A-Z)</li>
              <li className={/[a-z]/.test(password) ? 'valid' : ''}>Lowercase letter (a-z)</li>
              <li className={/[!@#$%^&*(),.?":{}|<>-]/.test(password) ? 'valid' : ''}>Special character (!@#$%...)</li>
            </ul>
          </div>
        </div>

        <button type="submit" className="auth-button" disabled={loading || !!passwordError}>
          {loading ? 'Saving...' : 'Set New Password'}
        </button>
      </form>

      <p className="auth-toggle">
        Link not working?{' '}
        <button type="button" className="link-button" onClick={onRequestNewLink} disabled={loading}>
          Request a new one
        </button>
        {' · '}
        <button type="button" className="link-button" onClick={onDone} disabled={loading}>
          Back to sign in
        </button>
      </p>
    </div>
  );
};

ResetPasswordForm.propTypes = {
  oobCode: PropTypes.string.isRequired,
  onDone: PropTypes.func.isRequired,
  onRequestNewLink: PropTypes.func.isRequired
};

export default ResetPasswordForm;
